import MobileOptionMenu from 'app/pages/mobile/components/mobile-option-menu/MobileOptionMenu';
import { useAgaveCodingDropdown } from 'app/hooks/useAgaveCodingDropdown';
import { useFlexCodingDropdown } from 'app/hooks/useFlexCodingDropdown';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';
import { useAccountType } from 'app/hooks/useAccountType';
import { useCodingDisplay } from 'app/hooks/useCodingDisplay';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'app/store';
import { OptionItem } from 'app/pages/mobile/components/mobile-option-menu/MobileOptionList';
import { useEffect, useMemo, useState } from 'react';
import { type CodingOptionKey } from 'app/pages/mobile/types/mobileCodingTypes';
import { listErpItems, type ErpPostingAccount } from 'app/services/erp-connection.service';
import {
  FlexGlCode,
  FlexCostCode,
  FlexCostType,
  FlexCode,
  FlexVendor,
  HighnoteTransactionEvent,
  AgaveGlCode,
  AgaveProject,
  AgaveCostCode,
  AgaveCostType,
  AgaveSubJob,
  AgaveVendor,
  AgaveLocation,
  AgaveDepartment,
  AgaveSubaccount,
  AgaveEmployeeEntity,
  AgaveEquipment,
  AgaveEmCostCode,
  AgaveEmCostType,
} from 'API';

interface MobileCodingMenuProps {
  open: boolean;
  onClose: () => void;
  codingType: CodingOptionKey | null;
  onSelect: (
    option:
      | FlexGlCode
      | FlexCode
      | FlexCostCode
      | FlexCostType
      | FlexVendor
      | AgaveGlCode
      | AgaveProject
      | AgaveCostCode
      | AgaveCostType
      | AgaveSubJob
      | AgaveVendor
      | AgaveLocation
      | AgaveDepartment
      | AgaveSubaccount
      | AgaveEmployeeEntity
      | AgaveEquipment
      | AgaveEmCostCode
      | AgaveEmCostType
      | ErpPostingAccount
  ) => void;
  transaction?: HighnoteTransactionEvent;
  selectedGlCode?: FlexGlCode | AgaveGlCode | null;
  selectedProject?: FlexCode | AgaveProject | null;
  selectedCostCode?: FlexCostCode | AgaveCostCode | null;
  selectedCostType?: FlexCostType | AgaveCostType | null;
  selectedSubJob?: AgaveSubJob | null;
  selectedVendor?: FlexVendor | AgaveVendor | null;
  selectedLocation?: AgaveLocation | null;
  selectedDepartment?: AgaveDepartment | null;
  selectedSubaccount?: AgaveSubaccount | null;
  selectedEmployee?: AgaveEmployeeEntity | null;
  selectedEquipment?: AgaveEquipment | null;
  /** SEE-975: selected EM cost code — scopes the EM cost-type list to valid pairs. */
  selectedEmCostCode?: AgaveEmCostCode | null;
  getLabel?: (columnId: CodingOptionKey) => string;
}

const MobileCodingMenu = ({
  open,
  onClose,
  codingType,
  onSelect,
  transaction,
  selectedGlCode,
  selectedProject,
  selectedCostCode,
  selectedSubJob,
  selectedLocation,
  selectedEmployee,
  selectedEquipment,
  selectedEmCostCode,
  getLabel,
}: MobileCodingMenuProps) => {
  const { t } = useTranslation();
  const { selectedPaidolId, selectedPaidol } = useAppSelector(selectUserCompanies);
  const { hasErpCodingUI, isConstructionType, isFlexCodingEnabled } = useAccountType();
  const { getDisplayName } = useCodingDisplay();

  const useAgave = hasErpCodingUI && isConstructionType;
  const useFlex = isFlexCodingEnabled;

  // SEE-944 — gates the employee->location->project cascade for Sage Intacct.
  // When off we preserve SEE-808's "show all" behavior on locations/projects.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const erpSetting = (selectedPaidol as any)?.erpDepartmentLocationSetting;
  const filterProjectsByEmployeeLocation = Boolean(
    erpSetting?.isEnabled &&
      erpSetting?.locationEnabled &&
      erpSetting?.filterProjectsByEmployeeLocationEnabled
  );

  const agaveGl = useAgaveCodingDropdown<AgaveGlCode>({
    codingType: 'glCodes',
    paidolId: selectedPaidolId,
    cardGroupId: transaction?.cardGroupId || undefined,
    enabled: useAgave && codingType === 'glCode',
  });

  const agaveProjects = useAgaveCodingDropdown<AgaveProject>({
    codingType: 'projects',
    paidolId: selectedPaidolId,
    cardGroupId: transaction?.cardGroupId || undefined,
    // SEE-944 — narrow projects to the form's currently-selected location
    // when the toggle is on. Read from `selectedLocation` (form state), not
    // `transaction.agaveLocation` (DB state) so the filter follows the user's
    // in-progress edits before save.
    locationId: filterProjectsByEmployeeLocation
      ? (selectedLocation as AgaveLocation | undefined)?.id
      : undefined,
    enabled: useAgave && codingType === 'project',
  });

  const agaveCostCodes = useAgaveCodingDropdown<AgaveCostCode>({
    codingType: 'costCodes',
    paidolId: selectedPaidolId,
    projectId: selectedProject?.id,
    subJobId: selectedSubJob?.id,
    enabled: useAgave && codingType === 'costCode',
  });

  const agaveCostTypes = useAgaveCodingDropdown<AgaveCostType>({
    codingType: 'costTypes',
    paidolId: selectedPaidolId,
    projectId: selectedProject?.id,
    costCodeId: selectedCostCode?.id,
    enabled: useAgave && codingType === 'costType',
  });

  const agaveVendors = useAgaveCodingDropdown<AgaveVendor>({
    codingType: 'vendors',
    paidolId: selectedPaidolId,
    enabled: useAgave && codingType === 'vendor',
  });

  const agaveSubJobs = useAgaveCodingDropdown<AgaveSubJob>({
    codingType: 'subJobs',
    paidolId: selectedPaidolId,
    projectId: selectedProject?.id,
    enabled: useAgave && codingType === 'subJob',
  });
  const agaveLocations = useAgaveCodingDropdown<AgaveLocation>({
    codingType: 'locations',
    paidolId: selectedPaidolId,
    projectId: selectedProject?.id,
    // SEE-944 — only narrow locations by employee when the paidol toggle is on,
    // otherwise preserve SEE-808's "show all locations" behavior. Read from
    // `selectedEmployee` (form state), not the DB-backed transaction.
    employeeId: filterProjectsByEmployeeLocation
      ? (selectedEmployee as AgaveEmployeeEntity | undefined)?.id
      : undefined,
    enabled: useAgave && codingType === 'location',
  });

  const agaveDepartments = useAgaveCodingDropdown<AgaveDepartment>({
    codingType: 'departments',
    paidolId: selectedPaidolId,
    projectId: selectedProject?.id,
    enabled: useAgave && codingType === 'department',
  });

  const agaveSubaccounts = useAgaveCodingDropdown<AgaveSubaccount>({
    codingType: 'subaccounts',
    paidolId: selectedPaidolId,
    enabled: useAgave && codingType === 'subaccount',
  });

  const agaveEmployees = useAgaveCodingDropdown<AgaveEmployeeEntity>({
    codingType: 'employees',
    paidolId: selectedPaidolId,
    locationId: transaction?.agaveLocation?.id,
    enabled: useAgave && codingType === 'employee',
  });

  // SEE-1435: equipment searches server-side like every other coding entity.
  // It used to share useEquipmentCodingOptions' cached fetch, which capped the
  // list at 500 rows and hid most of a large fleet.
  const agaveEquipment = useAgaveCodingDropdown<AgaveEquipment>({
    codingType: 'equipment',
    paidolId: selectedPaidolId,
    enabled: useAgave && codingType === 'equipment',
  });

  // SEE-1325: the EM lists are server-side searched now, like every other coding
  // entity. The EMGroup / department / cost-code-pair cascade that used to run in
  // a memo below is applied by the resolvers, so these are plain hooks.
  const equipmentForScope = selectedEquipment as AgaveEquipment | undefined;

  const agaveEmCostCodes = useAgaveCodingDropdown<AgaveEmCostCode>({
    codingType: 'emCostCodes',
    paidolId: selectedPaidolId,
    emGroup: equipmentForScope?.emGroup ?? undefined,
    emCo: equipmentForScope?.emCo ?? undefined,
    department: equipmentForScope?.department ?? undefined,
    enabled: useAgave && codingType === 'emCostCode',
  });

  const agaveEmCostTypes = useAgaveCodingDropdown<AgaveEmCostType>({
    codingType: 'emCostTypes',
    paidolId: selectedPaidolId,
    emGroup: equipmentForScope?.emGroup ?? undefined,
    emCo: equipmentForScope?.emCo ?? undefined,
    department: equipmentForScope?.department ?? undefined,
    emCostCodeId: selectedEmCostCode?.id ?? undefined,
    enabled: useAgave && codingType === 'emCostType',
  });

  const agaveHooks = {
    glCode: agaveGl,
    project: agaveProjects,
    costCode: agaveCostCodes,
    costType: agaveCostTypes,
    subJob: agaveSubJobs,
    vendor: agaveVendors,
    location: agaveLocations,
    department: agaveDepartments,
    subaccount: agaveSubaccounts,
    employee: agaveEmployees,
    equipment: agaveEquipment,
    emCostCode: agaveEmCostCodes,
    emCostType: agaveEmCostTypes,
  };
  const currentAgaveHook = codingType ? agaveHooks[codingType as keyof typeof agaveHooks] : null;

  // SEE-1135 — billable items are a single cached fetch (listErpItems), no
  // search/pagination, so they bypass the useAgaveCodingDropdown machinery like
  // equipment. Fetched on open; deduped in the service across all callers.
  const isBillableItemType = codingType === 'billableItem';
  const [billableItems, setBillableItems] = useState<ErpPostingAccount[]>([]);
  const [billableItemsLoading, setBillableItemsLoading] = useState(false);
  useEffect(() => {
    if (!open || !isBillableItemType || !useAgave || !selectedPaidolId) return;
    let cancelled = false;
    setBillableItemsLoading(true);
    listErpItems(selectedPaidolId, selectedPaidol?.erpConnectionId)
      .then((items) => {
        if (!cancelled) setBillableItems(items);
      })
      .catch(() => {
        if (!cancelled) setBillableItems([]);
      })
      .finally(() => {
        if (!cancelled) setBillableItemsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, isBillableItemType, useAgave, selectedPaidolId, selectedPaidol?.erpConnectionId]);

  const flexGl = useFlexCodingDropdown<FlexGlCode>({
    codingType: 'glCodes',
    paidolId: selectedPaidolId,
    cardGroupId: transaction?.cardGroupId || undefined,
    selectedGlCode,
    selectedProject,
    enabled: useFlex && codingType === 'glCode',
  });

  const flexProjects = useFlexCodingDropdown<FlexCode>({
    codingType: 'projects',
    paidolId: selectedPaidolId,
    cardGroupId: transaction?.cardGroupId || undefined,
    selectedGlCode,
    selectedProject,
    enabled: useFlex && codingType === 'project',
  });

  const flexCostCodes = useFlexCodingDropdown<FlexCostCode>({
    codingType: 'costCodes',
    paidolId: selectedPaidolId,
    selectedGlCode,
    selectedProject,
    enabled: useFlex && codingType === 'costCode',
  });

  const flexCostTypes = useFlexCodingDropdown<FlexCostType>({
    codingType: 'costTypes',
    paidolId: selectedPaidolId,
    selectedGlCode,
    selectedProject,
    enabled: useFlex && codingType === 'costType',
  });

  const flexVendors = useFlexCodingDropdown<FlexVendor>({
    codingType: 'vendors',
    paidolId: selectedPaidolId,
    selectedGlCode,
    selectedProject,
    enabled: useFlex && codingType === 'vendor',
  });

  const flexHooks = {
    glCode: flexGl,
    project: flexProjects,
    costCode: flexCostCodes,
    costType: flexCostTypes,
    vendor: flexVendors,
  };

  // SubJob, Location, Department, and Employee are Agave-only
  const agaveOnlyTypes = ['subJob', 'location', 'department', 'subaccount', 'employee'];
  const currentFlexHook =
    !codingType || agaveOnlyTypes.includes(codingType)
      ? null
      : flexHooks[codingType as keyof typeof flexHooks];

  const isLoading = useMemo(() => {
    if (isBillableItemType) {
      return billableItemsLoading;
    } else if (useAgave && currentAgaveHook) {
      return currentAgaveHook.isSearching;
    } else if (useFlex && currentFlexHook) {
      return currentFlexHook.isSearching;
    }
    return false;
  }, [isBillableItemType, billableItemsLoading, useAgave, useFlex, currentAgaveHook, currentFlexHook]);

  const menuOptions: OptionItem[] = useMemo(() => {
    // SEE-1135 — billable items aren't Agave/Flex entities; label them directly
    // ("Name - (ITEMID)") rather than via getDisplayName.
    if (isBillableItemType) {
      return billableItems.map((item) => ({
        name:
          item.accountName && item.accountName !== item.accountId
            ? `${item.accountName} - (${item.accountId})`
            : item.accountId,
        data: item,
        onSelect: () => {
          onSelect(item);
          onClose();
        },
      }));
    }

    let items: Array<
      | FlexGlCode
      | FlexCode
      | FlexCostCode
      | FlexCostType
      | FlexVendor
      | AgaveGlCode
      | AgaveProject
      | AgaveCostCode
      | AgaveCostType
      | AgaveSubJob
      | AgaveVendor
      | AgaveLocation
      | AgaveDepartment
      | AgaveSubaccount
      | AgaveEmployeeEntity
      | AgaveEquipment
      | AgaveEmCostCode
      | AgaveEmCostType
    > = [];

    if (useAgave && currentAgaveHook) {
      items = currentAgaveHook.currentItems;
    } else if (useFlex && currentFlexHook) {
      items = currentFlexHook.currentItems;
    }

    return items.map((item) => ({
      name: getDisplayName(item),
      data: item,
      onSelect: () => {
        onSelect(item);
        onClose();
      },
    }));
  }, [
    isBillableItemType,
    billableItems,
    useAgave,
    useFlex,
    currentAgaveHook,
    currentFlexHook,
    onSelect,
    onClose,
    getDisplayName,
  ]);

  const getTitle = () => {
    if (!codingType) return '';
    if (getLabel) {
      return t('mobileTransactionEdit.selectField', 'Select {{fieldName}}', {
        fieldName: getLabel(codingType),
      });
    }
    switch (codingType) {
      case 'glCode':
        return t('mobileTransactionEdit.selectGLAccount');
      case 'project':
        return t('mobileTransactionEdit.selectProject');
      case 'costCode':
        return t('mobileTransactionEdit.selectCostCode');
      case 'costType':
        return t('mobileTransactionEdit.selectCostType');
      case 'subJob':
        return t('mobileTransactionEdit.selectSubJob');
      case 'vendor':
        return t('mobileTransactionEdit.selectVendor');
      case 'location':
        return t('mobileTransactionEdit.selectLocation');
      case 'department':
        return t('mobileTransactionEdit.selectDepartment', 'Select Department');
      case 'subaccount':
        return t('mobileTransactionEdit.selectSubaccount', 'Select Subaccount');
      case 'employee':
        return t('mobileTransactionEdit.selectEmployee', 'Select Employee');
      case 'equipment':
        return t('mobileTransactionEdit.selectEquipment', 'Select Equipment');
      case 'emCostCode':
        return t('mobileTransactionEdit.selectEmCostCode', 'Select Equipment Cost Code');
      case 'emCostType':
        return t('mobileTransactionEdit.selectEmCostType', 'Select Equipment Cost Type');
      default:
        return t('mobileTransactionEdit.select', 'Select');
    }
  };

  // SEE-1325: the EM lists now search and paginate like every other coding
  // entity, so they route through currentAgaveHook rather than being excluded.
  const activeHook =
    useAgave && currentAgaveHook ? currentAgaveHook : useFlex && currentFlexHook ? currentFlexHook : null;

  const isLoadingMore = activeHook?.isLoadingMore ?? false;
  const showLoadingOverlay = isLoading && !isLoadingMore;
  const hasMore = activeHook?.hasMore ?? false;
  const onLoadMore = activeHook?.handleLoadMore;

  return (
    <MobileOptionMenu
      open={open}
      onClose={onClose}
      title={getTitle()}
      options={menuOptions}
      onSelect={(option) => {
        onSelect(option.data);
        onClose();
      }}
      searchable={(useAgave || useFlex) && !isBillableItemType}
      searchPlaceholder={t('mobileSearch')}
      onSearch={activeHook?.handleSearch}
      onClearSearch={activeHook?.handleClearSearch}
      searchDebounceMs={800}
      maskOptionNames={codingType === 'employee'}
      hasMore={hasMore}
      onLoadMore={onLoadMore}
      isLoadingMore={isLoadingMore}
      isLoading={showLoadingOverlay}
      sx={{
        pb: 0,
      }}
    />
  );
};

export default MobileCodingMenu;
